import React, { useEffect, useMemo, useRef, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";
import {
  ShieldCheck,
  Search,
  Image as ImageIcon,
  Send,
  Loader2,
  X,
  User as UserIcon,
  CheckCircle2,
  Eye,
  Pencil,
  ChevronRight,
  ChevronDown,
  MessageCircle,
} from "lucide-react";
import {
  listArchitectDmUsers,
  uploadArchitectDmImage,
  sendArchitectDm,
  listArchitectDmSent,
  architectDmAttachmentUrl,
} from "../lib/architectDm";
import { useAuth } from "../contexts/AuthContext";

function useDebounced(value, delay = 250) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
}

function formatWhen(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

export default function ArchitectMessages() {
  const { user } = useAuth();

  const [query, setQuery] = useState("");
  const debounced = useDebounced(query, 250);
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  const [recipient, setRecipient] = useState(null);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [image, setImage] = useState(null); // {path, content_type, previewUrl}
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const [sent, setSent] = useState([]);
  const [loadingSent, setLoadingSent] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);

  const loadUsers = React.useCallback(() => {
    setLoadingUsers(true);
    listArchitectDmUsers(debounced || undefined, 50)
      .then((d) => setUsers(d?.users || []))
      .catch(() => setUsers([]))
      .finally(() => setLoadingUsers(false));
  }, [debounced]);

  const loadSent = React.useCallback(() => {
    setLoadingSent(true);
    listArchitectDmSent(30)
      .then((d) => setSent(d?.messages || []))
      .catch(() => setSent([]))
      .finally(() => setLoadingSent(false));
  }, []);

  useEffect(() => {
    if (user?.is_architect) loadUsers();
  }, [user?.is_architect, loadUsers]);
  useEffect(() => {
    if (user?.is_architect) loadSent();
  }, [user?.is_architect, loadSent]);

  const canSend =
    recipient && title.trim().length > 0 && body.trim().length > 0 && !sending;

  const onPickImage = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) {
      toast.error("Image must be under 5 MB");
      return;
    }
    setUploading(true);
    try {
      const res = await uploadArchitectDmImage(f);
      setImage({
        path: res.path,
        content_type: res.content_type,
        previewUrl: URL.createObjectURL(f),
        name: f.name,
      });
      toast.success("Image uploaded");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const onSend = async () => {
    if (!canSend) return;
    setSending(true);
    try {
      await sendArchitectDm({
        recipient_email: recipient.email,
        recipient_user_id: recipient.user_id,
        title: title.trim(),
        body: body.trim(),
        image_path: image?.path || null,
        image_content_type: image?.content_type || null,
      });
      toast.success(`Sent to ${recipient.email || recipient.user_id}`);
      setTitle("");
      setBody("");
      setImage(null);
      loadSent();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Send failed");
    } finally {
      setSending(false);
    }
  };

  const filteredUsers = useMemo(
    () => users.filter((u) => u.user_id !== user?.user_id),
    [users, user?.user_id]
  );

  if (!user) return null;
  if (!user.is_architect) {
    return <Navigate to="/inbox" replace />;
  }

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 sm:py-10 space-y-8">
      <header className="flex items-center gap-3 flex-wrap">
        <ShieldCheck className="h-5 w-5 text-amber-400" />
        <h1 className="font-heading text-lg tracking-[0.25em] text-zinc-100">
          ARCHITECT · DIRECT MESSAGES
        </h1>
        <span className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-mono">
          send a note to any registered user
        </span>
        <Link
          to="/inbox"
          data-testid="architect-messages-inbox-link"
          className="ml-auto text-[10px] uppercase tracking-[0.25em] text-zinc-400 hover:text-amber-300 font-mono border border-zinc-800 hover:border-amber-500/40 px-2 py-1"
        >
          my inbox →
        </Link>
      </header>

      <section className="grid grid-cols-1 md:grid-cols-[280px_1fr] gap-6">
        {/* Recipient picker */}
        <aside className="border border-zinc-800 bg-[#0b0b0d]">
          <div className="border-b border-zinc-900 p-3">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-zinc-500" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search email or name…"
                data-testid="architect-messages-user-search"
                className="w-full pl-7 pr-2 py-1.5 bg-transparent border border-zinc-800 text-[12px] font-mono text-zinc-200 placeholder:text-zinc-600 focus:outline-none focus:border-amber-500/50"
              />
            </div>
          </div>
          <div className="max-h-[420px] overflow-y-auto">
            {loadingUsers && (
              <div className="p-3 text-[11px] text-zinc-500 font-mono flex items-center gap-2">
                <Loader2 className="h-3 w-3 animate-spin" /> loading users…
              </div>
            )}
            {!loadingUsers && filteredUsers.length === 0 && (
              <div className="p-3 text-[11px] text-zinc-500 font-mono">
                No users match.
              </div>
            )}
            {filteredUsers.map((u) => {
              const active = recipient?.user_id === u.user_id;
              return (
                <button
                  key={u.user_id}
                  type="button"
                  onClick={() => setRecipient(u)}
                  data-testid={`architect-messages-user-${u.user_id}`}
                  className={`w-full text-left px-3 py-2 border-l-2 transition-colors ${
                    active
                      ? "border-amber-500 bg-amber-500/5"
                      : "border-transparent hover:bg-zinc-900/50"
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <UserIcon className="h-3 w-3 text-zinc-500 shrink-0" />
                    <span className="text-[11px] font-mono text-zinc-200 truncate">
                      {u.email || u.user_id}
                    </span>
                  </div>
                  {u.name && (
                    <div className="text-[10px] text-zinc-500 font-mono truncate pl-5">
                      {u.name}
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </aside>

        {/* Compose */}
        <div className="border border-zinc-800 bg-[#0b0b0d] p-4 space-y-3">
          {recipient ? (
            <div className="flex items-center gap-2 text-[11px] font-mono">
              <span className="text-zinc-500 uppercase tracking-[0.25em]">
                to
              </span>
              <span className="px-2 py-0.5 border border-amber-500/40 bg-amber-500/5 text-amber-300">
                {recipient.email || recipient.user_id}
              </span>
              <button
                type="button"
                onClick={() => setRecipient(null)}
                className="text-zinc-500 hover:text-amber-300"
                data-testid="architect-messages-clear-recipient"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          ) : (
            <div className="text-[11px] font-mono text-zinc-500 uppercase tracking-[0.25em]">
              Select a user on the left to begin.
            </div>
          )}
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value.slice(0, 140))}
            placeholder="Title (max 140 chars)"
            data-testid="architect-messages-title-input"
            className="w-full px-3 py-2 bg-transparent border border-zinc-800 text-[13px] font-mono text-zinc-100 placeholder:text-zinc-600 focus:outline-none focus:border-amber-500/50"
          />
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] font-mono">
            <button
              type="button"
              onClick={() => setPreviewMode(false)}
              data-testid="architect-messages-tab-write"
              className={`inline-flex items-center gap-1 px-2 py-1 border ${
                !previewMode
                  ? "border-amber-500/50 text-amber-300 bg-amber-500/5"
                  : "border-zinc-800 text-zinc-500 hover:text-zinc-300"
              }`}
            >
              <Pencil className="h-3 w-3" /> write
            </button>
            <button
              type="button"
              onClick={() => setPreviewMode(true)}
              disabled={!body.trim()}
              data-testid="architect-messages-tab-preview"
              className={`inline-flex items-center gap-1 px-2 py-1 border disabled:opacity-40 disabled:cursor-not-allowed ${
                previewMode
                  ? "border-amber-500/50 text-amber-300 bg-amber-500/5"
                  : "border-zinc-800 text-zinc-500 hover:text-zinc-300"
              }`}
            >
              <Eye className="h-3 w-3" /> preview
            </button>
            <span className="ml-auto text-zinc-600 normal-case tracking-normal">
              markdown supported: **bold** · [link](url) · `code` · lists
            </span>
          </div>
          {previewMode ? (
            <div
              data-testid="architect-messages-body-preview"
              className="min-h-[180px] px-3 py-2 border border-zinc-800 bg-[#08080a] prose prose-invert prose-sm max-w-none prose-a:text-amber-300 prose-strong:text-zinc-100 prose-code:text-amber-200 prose-code:bg-zinc-900 prose-code:px-1 prose-code:rounded-sm prose-headings:text-zinc-100 prose-hr:border-zinc-800 prose-blockquote:border-l-amber-500/40 prose-blockquote:text-zinc-400"
            >
              <ReactMarkdown
                components={{
                  a: ({ node, ...props }) => (
                    <a
                      {...props}
                      target="_blank"
                      rel="noreferrer noopener"
                    />
                  ),
                }}
              >
                {body || "*(empty)*"}
              </ReactMarkdown>
            </div>
          ) : (
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value.slice(0, 4000))}
              rows={8}
              placeholder="Message body — Markdown supported (max 4000 chars)…"
              data-testid="architect-messages-body-input"
              className="w-full px-3 py-2 bg-transparent border border-zinc-800 text-[13px] font-mono text-zinc-100 placeholder:text-zinc-600 focus:outline-none focus:border-amber-500/50 resize-y"
            />
          )}

          {/* Image attachment */}
          <div className="flex items-center gap-3 flex-wrap">
            {!image && (
              <label
                data-testid="architect-messages-image-picker"
                className="inline-flex items-center gap-1.5 px-2 py-1 border border-zinc-800 text-zinc-400 hover:text-amber-300 hover:border-amber-500/40 text-[10px] uppercase tracking-[0.25em] font-mono cursor-pointer"
              >
                {uploading ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                  <ImageIcon className="h-3 w-3" />
                )}
                {uploading ? "uploading…" : "attach image"}
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/png,image/jpeg,image/webp,image/gif"
                  className="hidden"
                  onChange={onPickImage}
                  disabled={uploading}
                />
              </label>
            )}
            {image && (
              <div
                data-testid="architect-messages-image-preview"
                className="flex items-center gap-3 border border-amber-500/30 bg-amber-500/5 px-2 py-1"
              >
                <img
                  src={image.previewUrl}
                  alt="attachment preview"
                  className="h-10 w-10 object-cover border border-zinc-800"
                />
                <div className="text-[10px] font-mono text-zinc-400">
                  <div className="truncate max-w-[180px]">{image.name}</div>
                  <div className="text-zinc-600">{image.content_type}</div>
                </div>
                <button
                  type="button"
                  onClick={() => setImage(null)}
                  className="text-zinc-500 hover:text-rose-300"
                  data-testid="architect-messages-image-clear"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            )}
            <span className="text-[10px] font-mono text-zinc-600">
              png · jpeg · webp · gif · max 5 MB
            </span>
          </div>

          <div className="flex items-center gap-3 pt-2 border-t border-zinc-900">
            <button
              type="button"
              onClick={onSend}
              disabled={!canSend}
              data-testid="architect-messages-send"
              className="inline-flex items-center gap-2 px-4 py-2 border border-amber-500/50 bg-amber-500/10 text-amber-200 hover:bg-amber-500/20 disabled:opacity-40 disabled:cursor-not-allowed text-[11px] uppercase tracking-[0.25em] font-mono"
            >
              {sending ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Send className="h-3.5 w-3.5" />
              )}
              send message
            </button>
            <span className="text-[10px] font-mono text-zinc-600">
              {body.length}/4000
            </span>
          </div>
        </div>
      </section>

      {/* Threads grouped by recipient */}
      <section className="space-y-3">
        <h2 className="text-[11px] uppercase tracking-[0.25em] text-zinc-400 font-mono flex items-center gap-2">
          <MessageCircle className="h-3 w-3" /> threads
          <span className="text-zinc-600 normal-case tracking-normal">
            · grouped by recipient
          </span>
          {loadingSent && <Loader2 className="h-3 w-3 animate-spin" />}
        </h2>
        {!loadingSent && sent.length === 0 && (
          <p className="text-[11px] text-zinc-500 font-mono">
            No threads yet. Send your first message above.
          </p>
        )}
        <ThreadList
          messages={sent}
          onOpenThread={(recipient) => setRecipient(recipient)}
        />
      </section>
    </div>
  );
}

function ThreadList({ messages, onOpenThread }) {
  // group by counterparty user_id (non-architect side)
  const threads = useMemo(() => {
    const map = new Map();
    for (const m of messages) {
      const isArchitectSent = m.direction === "architect_to_user";
      const key = isArchitectSent
        ? m.recipient_user_id || m.recipient_email
        : m.sender_user_id || m.sender_email;
      if (!key) continue;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(m);
    }
    return Array.from(map.entries())
      .map(([key, msgs]) => {
        msgs.sort((a, b) =>
          (a.created_at || "").localeCompare(b.created_at || "")
        );
        const latest = msgs[msgs.length - 1];
        // figure out the counterparty display
        const counterMsg =
          latest.direction === "architect_to_user"
            ? {
                email: latest.recipient_email,
                user_id: latest.recipient_user_id,
              }
            : {
                email: latest.sender_email,
                user_id: latest.sender_user_id,
              };
        const unreadFromUser = msgs.filter(
          (m) => m.direction === "user_to_architect" && !m.read_at
        ).length;
        return {
          key,
          email: counterMsg.email,
          user_id: counterMsg.user_id,
          latest,
          messages: msgs,
          unread: unreadFromUser,
        };
      })
      .sort((a, b) =>
        (b.latest.created_at || "").localeCompare(a.latest.created_at || "")
      );
  }, [messages]);

  const [open, setOpen] = useState({});

  return (
    <div className="space-y-2">
      {threads.map((t) => {
        const isOpen = Boolean(open[t.key]);
        return (
          <article
            key={t.key}
            data-testid={`architect-thread-${t.key}`}
            className={`border ${
              t.unread > 0 ? "border-amber-500/40" : "border-zinc-800"
            } bg-[#0b0b0d]`}
          >
            <button
              type="button"
              onClick={() =>
                setOpen((prev) => ({ ...prev, [t.key]: !prev[t.key] }))
              }
              className="w-full text-left px-3 py-2 flex items-center gap-2"
            >
              {isOpen ? (
                <ChevronDown className="h-3.5 w-3.5 text-zinc-500 shrink-0" />
              ) : (
                <ChevronRight className="h-3.5 w-3.5 text-zinc-500 shrink-0" />
              )}
              <UserIcon className="h-3.5 w-3.5 text-amber-300 shrink-0" />
              <span className="text-[12px] font-mono text-zinc-100 truncate">
                {t.email || t.user_id}
              </span>
              <span className="text-[10px] uppercase tracking-[0.25em] font-mono text-zinc-500">
                · {t.messages.length} msg
              </span>
              {t.unread > 0 && (
                <span
                  data-testid={`architect-thread-unread-${t.key}`}
                  className="text-[9px] uppercase tracking-[0.25em] font-mono px-1.5 py-0.5 border border-amber-500/50 text-amber-300 bg-amber-500/5"
                >
                  {t.unread} new reply
                </span>
              )}
              <span className="ml-auto text-[10px] font-mono text-zinc-600">
                {formatWhen(t.latest.created_at)}
              </span>
            </button>
            {isOpen && (
              <div className="border-t border-zinc-900 p-3 space-y-3">
                <button
                  type="button"
                  onClick={() =>
                    onOpenThread({
                      email: t.email,
                      user_id: t.user_id,
                    })
                  }
                  data-testid={`architect-thread-reply-${t.key}`}
                  className="inline-flex items-center gap-1.5 px-2 py-1 border border-amber-500/40 text-amber-300 hover:bg-amber-500/10 text-[10px] uppercase tracking-[0.25em] font-mono"
                >
                  <Send className="h-3 w-3" /> send follow-up
                </button>
                <div className="space-y-2">
                  {t.messages.map((m) => (
                    <div
                      key={m.id}
                      data-testid={`architect-thread-msg-${m.id}`}
                      className={`border ${
                        m.direction === "user_to_architect"
                          ? "border-emerald-500/30 bg-emerald-500/[0.03] ml-6"
                          : "border-amber-500/30 bg-amber-500/[0.03] mr-6"
                      } p-2.5`}
                    >
                      <div className="flex items-center gap-2 flex-wrap text-[9px] uppercase tracking-[0.25em] font-mono">
                        <span
                          className={
                            m.direction === "user_to_architect"
                              ? "text-emerald-300"
                              : "text-amber-300"
                          }
                        >
                          {m.direction === "user_to_architect"
                            ? m.sender_email || m.sender_user_id
                            : "You"}
                        </span>
                        <span className="text-zinc-600">
                          {formatWhen(m.created_at)}
                        </span>
                        {m.direction === "architect_to_user" && (
                          <span className="ml-auto text-zinc-500">
                            {m.read_at ? "read" : "unread"}
                          </span>
                        )}
                      </div>
                      <div className="mt-1 text-[13px] text-zinc-100">
                        {m.title}
                      </div>
                      <div className="text-[11px] text-zinc-400 line-clamp-4 whitespace-pre-wrap">
                        {m.body}
                      </div>
                      {m.image_path && (
                        <img
                          src={architectDmAttachmentUrl(m.image_path)}
                          alt="attachment"
                          className="mt-1 h-20 border border-zinc-800"
                          onError={(e) => {
                            e.currentTarget.style.display = "none";
                          }}
                        />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </article>
        );
      })}
    </div>
  );
}
